import React, { useState } from "react";
import { X, Eye, UserRound, ShieldCheck, Signature } from "lucide-react";

const resolveMediaUrl = (path) => {
  if (!path) return null;
  if (path.startsWith("http://") || path.startsWith("https://")) return path;

  const base =
    window.location.hostname === "127.0.0.1" ||
    window.location.hostname === "localhost"
      ? "http://127.0.0.1:8001"
      : "https://konstruct.world/checklists";

  const clean = path.startsWith("/") ? path : `/${path}`;
  return `${base}${clean}`;
};

const getLatestNameFromSubmissions = (detail, keys = []) => {
  for (const item of detail?.items || []) {
    for (const sub of item?.submissions || []) {
      for (const key of keys) {
        const value = String(sub?.[key] || "").trim();

        if (value) {
          return value;
        }
      }
    }
  }

  return "";
};

const getLatestCheckerIdFromSubmissions = (detail) => {
  for (const item of detail?.items || []) {
    for (const sub of item?.submissions || []) {
      if (sub?.checker_id) {
        return `User ${sub.checker_id}`;
      }
    }
  }

  return "";
};

const getLatestNameFromAttempts = (attempts, role) => {
  for (let i = (attempts || []).length - 1; i >= 0; i -= 1) {
    const questions = attempts[i]?.questions || [];

    for (const row of questions) {
      const source = role === "maker" ? row?.maker : row?.checker;
      const name = String(source?.actor_name || "").trim();

      if (name) {
        return name;
      }
    }
  }

  return "";
};

const getLatestSignatureFromAttempts = (attempts, role) => {
  for (let i = (attempts || []).length - 1; i >= 0; i -= 1) {
    const questions = attempts[i]?.questions || [];

    for (const row of questions) {
      const source = role === "maker" ? row?.maker : row?.checker;

      const signature =
        source?.signature_url ||
        source?.signature ||
        source?.signature_file ||
        "";

      if (signature) {
        return signature;
      }
    }
  }

  return "";
};

const getLatestSignedAtFromAttempts = (attempts, role) => {
  for (let i = (attempts || []).length - 1; i >= 0; i -= 1) {
    const questions = attempts[i]?.questions || [];

    for (const row of questions) {
      const source = role === "maker" ? row?.maker : row?.checker;

      const signature =
        source?.signature_url ||
        source?.signature ||
        source?.signature_file ||
        "";

      const createdAt =
        source?.created_at ||
        source?.signed_at ||
        "";

      if (hasValidSignature(signature) && createdAt) {
        return createdAt;
      }
    }
  }

  return "";
};

const formatSignedDateTime = (value) => {
  if (!value) return "";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "";
  }

  const formatter = new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
    timeZone: "Asia/Kolkata",
  });

  return formatter
    .format(date)
    .replace(",", "")
    .replace(/\b(am|pm)\b/i, (m) => m.toUpperCase());
};

const getSignOffUsers = (detail, attempts = []) => {
  const makerName =
    detail?.maker_name ||
    detail?.created_by_name ||
    detail?.created_by ||
    getLatestNameFromSubmissions(detail, [
      "latest_maker_name",
      "maker_name",
      "created_by_name",
    ]) ||
    getLatestNameFromAttempts(attempts, "maker") ||
    "Maker";

  const contractorSignOff = detail?.contractor_checker_signoff;
  const contractorChecker = contractorSignOff
    ? {
        role: "contractor_checker",
        label: "Contractor Verified by",
        name:
          contractorSignOff.actor_name ||
          detail?.contractor_checker_name ||
          "Contractor Checker",
        designation: "Contractor Safety Officer",
        signature: contractorSignOff.signature || "",
        signedAt: contractorSignOff.signed_at || "",
        action: contractorSignOff.action || "",
      }
    : null;

  let checkerName =
    detail?.checker_name ||
    detail?.verified_by_name ||
    detail?.approved_by_name ||
    getLatestNameFromSubmissions(detail, [
      "latest_checker_name",
      "checker_name",
      "approved_by_name",
    ]) ||
    getLatestNameFromAttempts(attempts, "checker") ||
    getLatestCheckerIdFromSubmissions(detail) ||
    "Checker";

  const makerSignature =
    detail?.maker_signature ||
    detail?.maker_signature_url ||
    getLatestSignatureFromAttempts(attempts, "maker") ||
    "";

  let checkerSignature =
    detail?.checker_signature ||
    detail?.checker_signature_url ||
    getLatestSignatureFromAttempts(attempts, "checker") ||
    "";

  const makerSignedAt =
    detail?.maker_signed_at ||
    getLatestSignedAtFromAttempts(attempts, "maker") ||
    "";

  let checkerSignedAt =
    detail?.checker_signed_at ||
    getLatestSignedAtFromAttempts(attempts, "checker") ||
    "";

  // Prevent contractor checker's earlier rejection/approval from leaking into the final internal checker card
  if (contractorChecker) {
    const isContractorDuplicate =
      (hasValidSignature(contractorChecker.signature) &&
        checkerSignature === contractorChecker.signature) ||
      (contractorChecker.name && checkerName === contractorChecker.name);

    if (isContractorDuplicate && detail?.status !== "completed") {
      checkerSignature = "";
      checkerSignedAt = "";
    }
  }

  return {
    maker: {
      role: "maker",
      label: "Checked by",
      name: makerName,
      designation: "Maker",
      signature: makerSignature,
      signedAt: makerSignedAt,
    },
    contractorChecker,
    checker: {
      role: "checker",
      label: "Verified by",
      name: checkerName,
      designation: "Checker",
      signature: checkerSignature,
      signedAt: checkerSignedAt,
    },
  };
};

const SignatureViewModal = ({ open, user, onClose }) => {
  if (!open || !user) return null;

  const signatureUrl = resolveMediaUrl(user.signature);

  return (
    <div className="fixed inset-0 z-[260] flex items-center justify-center bg-black/60 p-4">
      <div className="w-full max-w-xl overflow-hidden rounded-2xl bg-white shadow-2xl">
        <div className="flex items-start justify-between border-b px-5 py-4">
          <div>
            <h3 className="text-base font-bold text-foreground">
              {user.label}
            </h3>

            <p className="mt-1 text-sm text-muted-foreground">
              {user.name}{" "}
              {user.designation ? (
                <span className="font-medium">({user.designation})</span>
              ) : null}
            </p>

            {user.signedAt ? (
              <p className="mt-1 text-xs text-muted-foreground">
                Signed on:{" "}
                <span className="font-medium text-foreground">
                  {formatSignedDateTime(user.signedAt)}
                </span>
              </p>
            ) : null}
          </div>

          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-border p-2 text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="p-5">
          {signatureUrl ? (
            <div className="rounded-xl border border-border bg-muted/20 p-4">
              <img
                src={signatureUrl}
                alt={`${user.designation || user.label} signature`}
                className="mx-auto max-h-72 w-full object-contain"
              />
            </div>
          ) : (
            <div className="flex min-h-[180px] flex-col items-center justify-center rounded-xl border border-dashed border-border bg-muted/20 text-center">
              <Signature className="mb-2 h-8 w-8 text-muted-foreground" />

              <p className="text-sm font-semibold text-foreground">
                No signature available
              </p>

              <p className="mt-1 text-xs text-muted-foreground">
                Latest signature was not found for this user.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const hasValidSignature = (signature) => {
  if (!signature) return false;
  const value = String(signature).trim();
  if (!value) return false;
  return !["null", "undefined", "none"].includes(value.toLowerCase());
};

const SignOffRow = ({ user, onView }) => {
  const hasSignature = hasValidSignature(user?.signature);

  return (
    <div className="flex min-w-0 flex-col gap-3 rounded-xl border border-border bg-muted/20 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex min-w-0 flex-1 items-center gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
          <UserRound className="h-5 w-5" />
        </div>

        <div className="min-w-0 flex-1">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            {user.label}
          </p>

          <p className="mt-0.5 break-words text-sm font-bold leading-snug text-foreground">
            {user.name}
          </p>

          {user.designation ? (
            <p className="mt-0.5 text-[11px] text-muted-foreground">
              {user.designation}
            </p>
          ) : null}

          {user.signedAt ? (
            <p className="mt-1 text-[10px] text-slate-400">
              Signed on: {formatSignedDateTime(user.signedAt)}
            </p>
          ) : null}
        </div>
      </div>

      <button
        type="button"
        onClick={() => onView(user)}
        disabled={!hasSignature}
        className={`inline-flex shrink-0 items-center justify-center gap-2 rounded-lg px-3 py-2 text-xs font-semibold transition ${
          hasSignature
            ? "border border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100"
            : "cursor-not-allowed border border-border bg-muted text-muted-foreground"
        }`}
      >
        <Eye className="h-3.5 w-3.5" />
        View
      </button>
    </div>
  );
};

function SafetyChecklistSignOffSection({
  detail,
  attempts = [],
  title = "Sign Off",
  description = "Available checklist signatures.",
  className = "",
}) {
  const [signatureUser, setSignatureUser] = useState(null);

  if (!detail) return null;

  const users = getSignOffUsers(detail, attempts);

  const signedUsers = [
    users.maker,
    users.contractorChecker,
    users.checker,
  ].filter((user) => user && hasValidSignature(user?.signature));

  if (signedUsers.length === 0) {
    return null;
  }

  return (
    <>
      <div className={`rounded-xl border bg-card p-5 shadow-sm ${className}`}>
        <div className="mb-4">
          <h2 className="text-sm font-bold text-foreground">{title}</h2>

          {description ? (
            <p className="mt-1 text-xs text-muted-foreground">{description}</p>
          ) : null}
        </div>

        <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
          {signedUsers.map((user) => (
            <SignOffRow key={user.role} user={user} onView={setSignatureUser} />
          ))}
        </div>
      </div>

      <SignatureViewModal
        open={!!signatureUser}
        user={signatureUser}
        onClose={() => setSignatureUser(null)}
      />
    </>
  );
}

export default SafetyChecklistSignOffSection;
export {
  formatSignedDateTime,
  getSignOffUsers,
  hasValidSignature,
  SignOffRow,
  SignatureViewModal,
};
